import { FC, ReactChildren, useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';

// helpers
import classnames from 'classnames';

// styles
import styles from './SbSharedTitle.scss';

// constants
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
const { SEARCH, DESIGN } = pageTypes;
import { HeadingType } from 'dibs-controlled-heading/exports/HeadingLevel';

// stuff
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SbSharedTitle_itemSearch } from './__generated__/SbSharedTitle_itemSearch.graphql';

type Props = {
    itemSearch: SbSharedTitle_itemSearch;
    Heading?: HeadingType;
    showFollowButton?: boolean;
    showContentModule?: boolean;
    children?: ReactChildren;
    headerWrapperClass?: string;
};

const SbSharedTitleComponent: FC<Props> = ({
    itemSearch,
    Heading,
    showFollowButton,
    showContentModule,
    children,
    headerWrapperClass,
}) => {
    const header = itemSearch.meta?.header;
    const subHeader = itemSearch.meta?.subHeader;
    const pageType = itemSearch.pageType;
    const subHeaderStyles = classnames(styles.subHeader, {
        [styles.withFollowButton]: showFollowButton,
    });
    const hasSpellCorrectSearch = itemSearch.searchCorrections?.originalSearchTerm;
    let customSearchHeader = undefined;
    const { isMobile } = useContext(ServerVarsContext);
    if (hasSpellCorrectSearch) {
        customSearchHeader = (
            <FormattedMessage
                id="sb.SbSharedTitle.spellTitle"
                defaultMessage="Showing results for"
            />
        );
    } else if (isMobile) {
        customSearchHeader = (
            <FormattedMessage id="sb.SbSharedTitle.title" defaultMessage="Results for" />
        );
    }

    const HeadingTag = pageType === DESIGN && showContentModule ? 'h2' : 'h1';

    const headerTitle =
        pageType === SEARCH ? (
            <span>
                {customSearchHeader || header}{' '}
                <i className={styles.searchTerm} data-tn="search-term">
                    "{itemSearch.searchTerm}"
                </i>
            </span>
        ) : (
            header
        );

    const headerWrapperClasses = classnames(styles.headerWrapper, headerWrapperClass);

    return (
        <div className={styles.container}>
            <div className={headerWrapperClasses}>
                <HeadingTag className={styles.header} data-tn="header-title">
                    {headerTitle}
                </HeadingTag>
                {showFollowButton && children}
            </div>
            {subHeader && Heading && (
                <Heading className={subHeaderStyles} data-tn="header-sub-title">
                    {subHeader}
                </Heading>
            )}
        </div>
    );
};

SbSharedTitleComponent.defaultProps = {
    showContentModule: false,
};
export const SbSharedTitle = createFragmentContainer(SbSharedTitleComponent, {
    itemSearch: graphql`
        fragment SbSharedTitle_itemSearch on ItemSearchQueryConnection {
            totalResults
            pageType
            searchTerm
            searchCorrections {
                originalSearchTerm
            }
            meta {
                header
                subHeader
            }
        }
    `,
});
